import { Col, Row } from 'reactstrap';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Timeline from '../Timeline/Timeline';
import { useCreateTimelineSection } from '../Timeline/hooks/useCreateTimelineSection';
import invariant from 'helpers/invariant';
import PageTitle from 'components/PageTitle/PageTitle';
import { useAuthContext } from 'contexts/AuthContext';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import { NSButton } from 'bricks/NSButton/NSButton';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { FEATURE_IDS } from 'services/constants';
import { URLS } from 'services/urls';
import { AccessLevels } from 'types/auth';
import { IconExternalLink, IconStack2Filled } from '@tabler/icons-react';
import useToast from 'hooks/useToast';


const TasksHome = () => {
    const { selectedOrganizationId, userOrganizations, isFeatureAccessibleToUser } = useAuthContext();
    invariant(selectedOrganizationId, 'Selected organization ID is required');
    
    const { dealId } = useParams<{ dealId: string }>();
    invariant(dealId, 'Selected deal ID is required');

    const queryClient = useQueryClient();
    const { showError } = useToast();
    const { mutate: createTimelineSection, isPending: isCreatingSection } = useCreateTimelineSection();

    const handleAddSection = () => {
        createTimelineSection(
            { orgId: selectedOrganizationId!, dealId: Number(dealId) },
            {
                onSuccess: () => queryClient.invalidateQueries({
                    queryKey: ['timelineSections', selectedOrganizationId!, Number(dealId)],
                }),
                onError: () => showError('Failed to create section'),
            },
        );
    };

    const { data: fetchedDeal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: Number(dealId),
        shouldFetch: !!Number(dealId),
    });

    const selectedUserOrganization = userOrganizations.find(org => org.organization.correlationId === selectedOrganizationId);
    const userCanUpdateTemplate = isFeatureAccessibleToUser([FEATURE_IDS.DEAL_ORGANIZATION_MANAGEMENT], AccessLevels.READ_WRITE_ACCESS);
    const dueDiligenceTemplateUrl
            = `${URLS.PROJECTS_APP.ORGANIZATION_MANAGEMENT}/${selectedUserOrganization?.organization.id}/products/timeline-activities`;
    return (
        <>
            <PageTitle
                title="Tasks"
                displayBreadcrumb
                dealName={fetchedDeal?.dealName}
                pageMetaDataTitle="Tasks Home"
                containerClassName="mb-3"
            >
                <div className="d-flex align-items-center">
                    <NSButton
                        icon={IconStack2Filled}
                        color="link"
                        className="text-primary px-0 mr-3"
                        callback={handleAddSection}
                        disabled={isCreatingSection}
                        featureId={FEATURE_IDS.DEAL_DASHBOARD}
                        dataTestId="add-section-button"
                    >
                        <span className="ml-1">Add section</span>
                    </NSButton>
                    {selectedUserOrganization && (
                        userCanUpdateTemplate ? (
                            <a
                                href={dueDiligenceTemplateUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary d-inline-flex align-items-center heap-deal-dashboard-overview-manage-template"
                            >
                                <NSIcon icon={IconExternalLink} />
                                <span className="ml-1">Manage template</span>
                            </a>
                        ) : (
                            <NSButton
                                id="manage-template-link"
                                icon={IconExternalLink}
                                color="link"
                                className="text-primary px-0 heap-deal-dashboard-overview-manage-template"
                                featureId={FEATURE_IDS.DEAL_ORGANIZATION_MANAGEMENT}
                                disabled
                            >
                                <span className="ml-1">Manage template</span>
                            </NSButton>
                        )
                    )}
                </div>
            </PageTitle>
            <Row className="g-0 h-100">
                <Col md="12" className="d-flex flex-column h-100">
                    <div className="flex-grow-1 mt-3">
                        <Timeline dealId={Number(dealId)} />
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default TasksHome;
